import { useState, useEffect, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Inbox,
  Calendar,
  CheckSquare,
  FolderKanban,
  Tags,
  Users,
  LayoutGrid,
  StickyNote,
  NotebookPen,
  Search,
  Clock,
  Flag,
  History,
  PanelLeftClose,
  PanelLeft,
  Settings,
  Radio,
  Pin,
  ChevronDown,
  ChevronRight,
  Archive,
  Bell,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { usePreferences } from '../contexts/PreferencesContext';
import { apiGet, apiPatch } from '../lib/api';
import { CalendarWidget } from './CalendarWidget';

interface PinnedItem {
  id: string;
  entity_type: string;
  entity_id: string;
  label: string;
  sort_order: number;
}

interface RecentItem {
  path: string;
  title: string;
  type: 'task' | 'note' | 'meeting' | 'project' | 'view' | 'meeting-series';
  visitedAt: number;
}

const PIN_ICONS: Record<string, LucideIcon> = {
  task: CheckSquare,
  note: StickyNote,
  meeting_instance: Calendar,
  meeting_series: Calendar,
  scope: FolderKanban,
  view: LayoutGrid,
  person: Users,
  scratchpad: NotebookPen,
};

function getPinPath(pin: PinnedItem): string {
  switch (pin.entity_type) {
    case 'task': return `/tasks/${pin.entity_id}`;
    case 'note': return `/notes/${pin.entity_id}`;
    case 'meeting_series': return `/meetings/${pin.entity_id}`;
    case 'meeting_instance': return `/meetings/standalone/${pin.entity_id}`;
    case 'scope': return `/projects/${pin.entity_id}`;
    case 'view': return `/views/${pin.entity_id}`;
    case 'person': return `/people/${pin.entity_id}`;
    case 'scratchpad': return `/scratchpad/${pin.entity_id}`;
    default: return '/';
  }
}

const RECENT_ICONS: Record<RecentItem['type'], LucideIcon> = {
  task: CheckSquare,
  note: StickyNote,
  meeting: Calendar,
  'meeting-series': Calendar,
  project: FolderKanban,
  view: LayoutGrid,
};

const STORAGE_KEY = 'dossier-recent-items';
const MAX_RECENT = 5;

function getRecentItems(): RecentItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveRecentItems(items: RecentItem[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
}

/** Match a route to a trackable entity type, or null if not trackable. */
function matchRoute(pathname: string): { type: RecentItem['type']; idParam: string } | null {
  const patterns: [RegExp, RecentItem['type']][] = [
    [/^\/tasks\/([^/]+)$/, 'task'],
    [/^\/notes\/([^/]+)$/, 'note'],
    [/^\/meetings\/standalone\/([^/]+)$/, 'meeting'],
    [/^\/meetings\/[^/]+\/([^/]+)$/, 'meeting'],
    [/^\/meeting-instances\/([^/]+)$/, 'meeting'],
    [/^\/meetings\/([^/]+)$/, 'meeting-series'],
    [/^\/projects\/([^/]+)$/, 'project'],
    [/^\/views\/([^/]+)$/, 'view'],
  ];
  for (const [re, type] of patterns) {
    const m = pathname.match(re);
    if (m) return { type, idParam: m[1] };
  }
  return null;
}

interface SidebarProps {
  open: boolean;
  onToggle: () => void;
}

const navItems = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/inbox', icon: Inbox, label: 'Inbox' },
  { to: '/meetings', icon: Calendar, label: 'Meetings' },
  { to: '/tasks', icon: CheckSquare, label: 'Tasks' },
  { to: '/notes', icon: StickyNote, label: 'Notes' },
  { to: '/scratchpad', icon: NotebookPen, label: 'Scratchpad' },
  { to: '/projects', icon: FolderKanban, label: 'Projects' },
  { to: '/tags', icon: Tags, label: 'Tags' },
  { to: '/people', icon: Users, label: 'People' },
  { to: '/views', icon: LayoutGrid, label: 'Views' },
  { to: '/search', icon: Search, label: 'Search' },
  { to: '/reminders', icon: Bell, label: 'Reminders' },
  { to: '/timeline', icon: Clock, label: 'Timeline' },
  { to: '/flags', icon: Flag, label: 'Flags' },
  { to: '/history', icon: History, label: 'History' },
  { to: '/archive', icon: Archive, label: 'Archive' },
];

interface ActiveMeeting {
  id: string;
  series_id: string | null;
  title: string | null;
  meeting_date: string;
  meeting_time: string | null;
  duration_minutes: number | null;
  series_name: string | null;
}

export function Sidebar({ open, onToggle }: SidebarProps) {
  const location = useLocation();
  const { prefs } = usePreferences();
  const scratchpadEnabled = prefs['scratchpad.enabled'] !== 'false';
  const showRecent = prefs['sidebar.recentItems'] !== 'false';
  const recentCount = parseInt(prefs['sidebar.recentItemsCount'] || '5', 10) || MAX_RECENT;
  const showPins = prefs['sidebar.pinnedItems'] !== 'false';
  const showActiveMeetings = prefs['sidebar.activeMeetings'] !== 'false';
  const earlyMinutes = parseInt(prefs['sidebar.activeMeetingsEarlyMinutes'] || '30', 10) || 0;
  const [recentItems, setRecentItems] = useState<RecentItem[]>(getRecentItems);
  const [pinnedItems, setPinnedItems] = useState<PinnedItem[]>([]);
  const [pinsCollapsed, setPinsCollapsed] = useState(false);
  const [recentCollapsed, setRecentCollapsed] = useState(false);
  const [activeMeetings, setActiveMeetings] = useState<ActiveMeeting[]>([]);

  // Fetch pinned items
  useEffect(() => {
    if (!showPins) { setPinnedItems([]); return; }
    apiGet<PinnedItem[]>('/pins')
      .then(setPinnedItems)
      .catch(() => setPinnedItems([]));
  }, [showPins]);

  // Listen for pin changes (custom event from detail pages)
  useEffect(() => {
    function handlePinChange() {
      apiGet<PinnedItem[]>('/pins')
        .then(setPinnedItems)
        .catch(() => {});
    }
    window.addEventListener('dossier:pins-changed', handlePinChange);
    return () => window.removeEventListener('dossier:pins-changed', handlePinChange);
  }, []);

  // Fetch active meetings (poll every 60s)
  useEffect(() => {
    if (!showActiveMeetings) {
      setActiveMeetings([]);
      return;
    }
    function fetchActive() {
      apiGet<ActiveMeeting[]>(`/meeting-instances/active?early_minutes=${earlyMinutes}`)
        .then(setActiveMeetings)
        .catch(() => setActiveMeetings([]));
    }
    fetchActive();
    const interval = setInterval(fetchActive, 60_000);
    return () => clearInterval(interval);
  }, [showActiveMeetings, earlyMinutes]);

  // Track page title changes to capture the entity name + update pin labels
  const updateTitle = useCallback((path: string, title: string) => {
    setRecentItems(prev => {
      // Only update if the title actually changed
      const existing = prev.find(item => item.path === path);
      if (existing && existing.title === title) return prev;
      const updated = prev.map(item => item.path === path ? { ...item, title } : item);
      saveRecentItems(updated);
      return updated;
    });

    // Also update pin label if this entity is pinned
    const match = matchRoute(path);
    if (match) {
      setPinnedItems(prev => {
        const pin = prev.find(p => p.entity_id === match.idParam);
        if (pin && pin.label !== title) {
          apiPatch(`/pins/${pin.id}`, { label: title }).catch(() => {});
          return prev.map(p => p.id === pin.id ? { ...p, label: title } : p);
        }
        return prev;
      });
    }
  }, []);

  // Track navigation to entity detail pages
  useEffect(() => {
    const match = matchRoute(location.pathname);
    if (!match) return;

    setRecentItems(prev => {
      // If we already have this path with a title, just move it to top
      const existing = prev.find(item => item.path === location.pathname);
      const filtered = prev.filter(item => item.path !== location.pathname);
      const newItem: RecentItem = {
        path: location.pathname,
        title: existing?.title || '',
        type: match.type,
        visitedAt: Date.now(),
      };
      const updated = [newItem, ...filtered].slice(0, MAX_RECENT);
      saveRecentItems(updated);
      return updated;
    });

    // Watch for the page heading to appear AND keep watching for renames
    let lastTitle = '';
    const observer = new MutationObserver(() => {
      const h1 = document.querySelector('h1');
      if (h1) {
        const title = h1.textContent?.trim();
        if (title && title !== lastTitle) {
          lastTitle = title;
          updateTitle(location.pathname, title);
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });

    // Also check immediately in case h1 is already rendered
    const h1 = document.querySelector('h1');
    if (h1?.textContent?.trim()) {
      lastTitle = h1.textContent.trim();
      updateTitle(location.pathname, lastTitle);
    }

    return () => { observer.disconnect(); };
  }, [location.pathname, updateTitle]);

  return (
    <aside
      className={`bg-bg-secondary border-r border-border flex flex-col transition-all duration-200
        fixed inset-y-0 left-0 z-40 sm:relative sm:z-auto
        pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] sm:pt-0 sm:pb-0
        ${open ? 'w-[260px]' : 'w-0 overflow-hidden'
      }`}
    >
      <div className="flex items-center justify-between h-[52px] px-4 border-b border-border">
        <span className="text-lg font-semibold text-text-primary tracking-tight">
          Dossier
        </span>
        <button
          onClick={onToggle}
          className="p-1.5 rounded-md text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors"
        >
          {open ? <PanelLeftClose size={18} /> : <PanelLeft size={18} />}
        </button>
      </div>

      <nav className="flex-1 py-2 px-2 space-y-0.5 overflow-y-auto">
        {navItems
          .filter(item => item.to !== '/scratchpad' || scratchpadEnabled)
          .map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                isActive
                  ? 'bg-accent/10 text-accent'
                  : 'text-text-secondary hover:text-text-primary hover:bg-bg-hover'
              }`
            }
          >
            <Icon size={18} />
            <span>{label}</span>
          </NavLink>
        ))}

        {/* Active Meetings — below Timeline, inside nav */}
        {showActiveMeetings && activeMeetings.length > 0 && (
          <div className="pt-2 mt-2 border-t border-border">
            <span className="px-3 text-[10px] font-medium uppercase tracking-wider text-success flex items-center gap-1">
              <Radio size={10} className="animate-pulse" /> Now
            </span>
            <div className="mt-1 space-y-0.5">
              {activeMeetings.map(m => (
                <NavLink
                  key={m.id}
                  to={m.series_id ? `/meetings/${m.series_id}/${m.id}` : `/meetings/standalone/${m.id}`}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-3 py-1.5 rounded-md text-xs transition-colors ${
                      isActive
                        ? 'bg-success/15 text-success'
                        : 'text-text-secondary hover:text-text-primary hover:bg-bg-hover'
                    }`
                  }
                >
                  <Calendar size={14} className="flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <span className="block truncate">{m.title || m.series_name || 'Meeting'}</span>
                    {m.meeting_time && (
                      <span className="text-[10px] text-text-muted">{formatSidebarTime(m.meeting_time)}</span>
                    )}
                  </div>
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Calendar Widget — bottom-aligned above Recent Items */}
      <div className="border-t border-border">
        <CalendarWidget />
      </div>

      {/* Pinned Items */}
      {showPins && pinnedItems.length > 0 && (
        <div className="border-t border-border px-2 py-2">
          <button
            onClick={() => setPinsCollapsed(prev => !prev)}
            className="flex items-center gap-1 px-3 text-[10px] font-medium uppercase tracking-wider text-text-muted hover:text-text-secondary transition-colors w-full text-left"
          >
            {pinsCollapsed ? <ChevronRight size={10} /> : <ChevronDown size={10} />}
            <Pin size={10} />
            Pinned
          </button>
          {!pinsCollapsed && (
            <div className="mt-1 space-y-0.5">
              {pinnedItems.map(pin => {
                const Icon = PIN_ICONS[pin.entity_type] || Pin;
                const path = getPinPath(pin);
                return (
                  <NavLink
                    key={pin.id}
                    to={path}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 px-3 py-1.5 rounded-md text-xs transition-colors ${
                        isActive
                          ? 'bg-accent/10 text-accent'
                          : 'text-text-muted hover:text-text-secondary hover:bg-bg-hover'
                      }`
                    }
                  >
                    <Icon size={14} className="flex-shrink-0" />
                    <span className="truncate">{pin.label}</span>
                  </NavLink>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Recent Items */}
      {showRecent && recentItems.length > 0 && (
        <div className="border-t border-border px-2 py-2">
          <button
            onClick={() => setRecentCollapsed(prev => !prev)}
            className="flex items-center gap-1 px-3 text-[10px] font-medium uppercase tracking-wider text-text-muted hover:text-text-secondary transition-colors w-full text-left"
          >
            {recentCollapsed ? <ChevronRight size={10} /> : <ChevronDown size={10} />}
            <Clock size={10} />
            Recent
          </button>
          {!recentCollapsed && (
            <div className="mt-1 space-y-0.5">
              {recentItems.slice(0, recentCount).map(item => {
                const Icon = RECENT_ICONS[item.type];
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 px-3 py-1.5 rounded-md text-xs transition-colors ${
                        isActive
                          ? 'bg-accent/10 text-accent'
                          : 'text-text-muted hover:text-text-secondary hover:bg-bg-hover'
                      }`
                    }
                  >
                    <Icon size={14} className="flex-shrink-0" />
                    <span className="truncate">{item.title || 'Untitled'}</span>
                  </NavLink>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="border-t border-border px-2 py-2 flex items-center justify-between">
        <NavLink
          to="/settings"
          className={({ isActive }) =>
            `flex items-center gap-2 px-3 py-1.5 rounded-md text-xs transition-colors ${
              isActive
                ? 'bg-accent/10 text-accent'
                : 'text-text-muted hover:text-text-secondary hover:bg-bg-hover'
            }`
          }
        >
          <Settings size={14} />
          <span>Settings</span>
        </NavLink>
        <span className="text-[10px] text-text-muted px-2">v{__APP_VERSION__}</span>
      </div>
    </aside>
  );
}

function formatSidebarTime(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${ampm}`;
}
